/* eslint-disable react-hooks/exhaustive-deps */
import {
  CheckIcon,
  ChevronDownIcon,
  CubeTransparentIcon,
  EyeIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/20/solid";
import { NewspaperIcon, PresentationChartBarIcon } from "@heroicons/react/24/outline";
import {
  Button,
  Menu,
  Paper, 
  Text,
} from "@mantine/core";
import {
  useEffect, useMemo, useState,
} from "react";
import uuid from "react-uuid";
import { ReactComponent as He } from "../../../assets/images/heSm.svg";
import { ReactComponent as Segment } from "../../../assets/images/segmentAvatar.svg";
import { Type } from "lucide-react";
import { IconWorld } from "@tabler/icons-react";

type Props = {
  list?: any[];
  selected: any;
  setSelected: any;
  placeholder?: string;
  type?: string;
  metricsList?: any[];
  className?: string;
  position?: "bottom" | "left" | "right" | "top" | "bottom-end" | "bottom-start" | "left-end" | "left-start" | "right-end" | "right-start" | "top-end" | "top-start";
};

function classNames(...classes: any) {
  return classes.filter(Boolean).join(" ");
}

const listOfSources = [
  {
    name: "all",
    label: "All Sources",
    icon: CubeTransparentIcon,
  },
  {
    name: "native",
    label: "Hyperengage",
    icon: He,
  },
  {
    name: "segment",
    label: "Segment",
    icon: Segment,
  },
];

const listOfAttributes = [
  {
    name: "all",
    label: "All Types",
    icon: CubeTransparentIcon,
  },
  {
    name: "traits",
    label: "Traits",
    icon: NewspaperIcon,
  },
  {
    name: "metrics",
    label: "Metrics",
    icon: PresentationChartBarIcon,
  },
];

const SingleSelectDropdown = ({
  list = [],
  metricsList = [],
  type = "Fields",
  selected,
  setSelected,
  position = "left-start",
  className = "",
  placeholder = "Select Property",
}: Props) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [query, setQuery] = useState<string>("");

  const [selectedSource, setSelectedSource] = useState(listOfSources[0].name);
  const [selectedAttribute, setSelectedAttribute] = useState<any>("all");
  const [queriedData, setQueriedData] = useState<any[]>([]);

  const displayTraits = useMemo(() => (selectedSource === "all"
    ? list : list?.filter((item: any) => item.source === selectedSource)
  ), [selectedSource, list?.length]);

  const displayMetrics = useMemo(() => (selectedSource === "all"
    ? metricsList : metricsList?.filter(
      (item: any) => item.source === selectedSource,
    )), [selectedSource, metricsList?.length]);

  useEffect(() => {
    let data = [];
    if (selectedAttribute === "metrics") {
      data = displayMetrics.filter(
        (item) => item?.display_name?.toLowerCase().includes(query.toLowerCase()),
      );
    }
    if (selectedAttribute === "traits") {
      data = displayTraits.filter(
        (item) => item?.display_name?.toLowerCase().includes(query.toLowerCase()),
      );
    }
    if (selectedAttribute === "all") {
      data = [...displayTraits, ...displayMetrics].filter(
        (item) => item?.display_name?.toLowerCase().includes(query.toLowerCase()),
      );
    }
    setQueriedData(data);
  }, [query, displayMetrics?.length, displayTraits?.length, selectedAttribute]);

  return (
    <Menu
      withinPortal
      closeOnClickOutside
      opened={isOpen}
      onChange={setIsOpen}
      position={position}
    >
      <Menu.Target>
        <div>
          <Button
            className={
            `${selected?.display_name
            && "text-gray-700 hover:bg-gray-50 hover:text-gray-800 hover:border-gray-300"} 
             ${className}
            `
          }
            color="white"
            radius="md"
            size="sm"
            leftSection={<EyeIcon className="w-4 h-4 text-gray-500" />}
            rightSection={<ChevronDownIcon className="w-3 h-3 text-gray-400" />}
            type="button"
            onClick={() => {
              setIsOpen((state) => !state);
            }}
          >
            <span className="text-sm font-medium truncate max-w-[180px]">
              {selected?.display_name || placeholder}
            </span>
          </Button>
        </div>
      </Menu.Target>
      <Menu.Dropdown className="z-10 p-0 origin-bottom-right">
        <div className="h-[300px] w-[320px] overflow-hidden flex flex-col rounded-md border border-gray-200">
          {/* Search bar with filter buttons */}
          <div className="flex items-center border-b border-gray-200 p-2">
            <div className="relative flex-1">
              <div
                className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                aria-hidden="true"
              >
                <MagnifyingGlassIcon
                  className="h-3.5 w-3.5 text-gray-400"
                  aria-hidden="true"
                />
              </div>
              <input
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                value={query}
                type="text"
                className="text-xs border border-gray-200 rounded-md focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder:text-xs text-gray-600 w-full pl-9 pr-3 py-1.5 focus:text-gray-900"
                placeholder={`Search ${type}`}
              />
            </div>
          </div>
          
          {/* Filter panel */}
          <div className="border-b border-gray-200 bg-gray-50 p-2">
            <div className="flex justify-between items-center mb-2">
              <div className="flex space-x-1">                
                <div className="relative">
                  <Menu position="bottom-start" offset={4} withinPortal={false}>
                    <Menu.Target>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<IconWorld className="h-3 w-3" />}
                        className="text-xs font-medium flex items-center space-x-1"
                        rightSection={<ChevronDownIcon className="h-3 w-3" />}
                      >
                        <span>{listOfSources.find(item => item.name === selectedSource)?.label}</span>
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown className="p-1 min-w-[120px] bg-gray-50">
                      {listOfSources.map((source) => (
                        <Menu.Item
                          key={source.name}
                          leftSection={<source.icon className="h-3.5 w-3.5 text-gray-500" />}
                          onClick={() => {
                            setSelectedSource(source.name);
                            if (selectedAttribute !== "all") {
                              setSelectedAttribute("all");
                            }
                          }}
                          className={`text-xs ${selectedSource === source.name ? 'bg-gray-50 text-primary-600' : ''}`}
                        >
                          {source.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </div>
                <div className="relative">
                  <Menu position="bottom-start" offset={4} withinPortal={false}>
                    <Menu.Target>
                      <Button
                        size="xs"
                        variant="light"
                        leftSection={<Type className="h-3 w-3" />}
                        className="text-xs font-medium flex items-center space-x-1"
                        rightSection={<ChevronDownIcon className="h-3 w-3" />}
                      >
                        <span>{listOfAttributes.find(item => item.name === selectedAttribute)?.label}</span>
                      </Button>
                    </Menu.Target>
                    <Menu.Dropdown className="p-1 min-w-[120px]">
                      {listOfAttributes.map((item) => (
                        <Menu.Item
                          key={item.name}
                          leftSection={<item.icon className="h-3.5 w-3.5 text-gray-500" />}
                          onClick={() => setSelectedAttribute(item.name)}
                          className={`text-xs ${selectedAttribute === item.name ? 'bg-gray-50 text-primary-600' : ''}`}
                        >
                          {item.label}
                        </Menu.Item>
                      ))}
                    </Menu.Dropdown>
                  </Menu>
                </div>
              </div>
            </div>
            
            {/* Active filters display */}
            {(selectedSource !== "all" || selectedAttribute !== "all") && (
              <div className="flex flex-wrap gap-1 mt-1">
                {selectedSource !== "all" && (
                  <div className={`
                    text-xs px-2 py-0.5 rounded-md flex items-center 
                    ${selectedSource === 'native' 
                      ? 'bg-primary-50 text-primary-700 border border-primary-100' 
                      : selectedSource === 'segment' 
                        ? 'bg-blue-50 text-blue-700 border border-blue-100' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }
                  `}
                  >
                    {(() => {
                      const source = listOfSources.find(s => s.name === selectedSource);
                      const SourceIcon = source?.icon;
                      return (
                        <>
                          {SourceIcon && <SourceIcon className={`
                            h-3 w-3 mr-1 
                            ${selectedSource === 'native' 
                              ? 'text-primary-500' 
                              : selectedSource === 'segment' 
                                ? 'text-blue-500' 
                                : 'text-gray-500'
                            }
                          `}
                          />}
                          <span className="mr-1">{source?.label}</span>
                        </>
                      );
                    })()} 
                    <button 
                      type="button" 
                      className={`
                        hover:opacity-80 
                        ${selectedSource === 'native' 
                          ? 'text-primary-500' 
                          : selectedSource === 'segment' 
                            ? 'text-blue-500' 
                            : 'text-gray-500'
                        }
                      `}
                      onClick={() => setSelectedSource("all")}
                    >
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
                {selectedAttribute !== "all" && (
                  <div className={`
                    text-xs px-2 py-0.5 rounded-md flex items-center 
                    ${selectedAttribute === 'traits' 
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' 
                      : selectedAttribute === 'metrics' 
                        ? 'bg-amber-50 text-amber-700 border border-amber-100' 
                        : 'bg-gray-100 text-gray-700 border border-gray-200'
                    }
                  `}
                  >
                    {(() => {
                      const attribute = listOfAttributes.find(a => a.name === selectedAttribute);
                      const AttributeIcon = attribute?.icon;
                      return (
                        <>
                          {AttributeIcon && <AttributeIcon className={`
                            h-3 w-3 mr-1 
                            ${selectedAttribute === 'traits' 
                              ? 'text-emerald-500' 
                              : selectedAttribute === 'metrics' 
                                ? 'text-amber-500' 
                                : 'text-gray-500'
                            }
                          `}
                          />}
                          <span className="mr-1">{attribute?.label}</span>
                        </>
                      );
                    })()}
                    <button 
                      type="button" 
                      className={`
                        hover:opacity-80 
                        ${selectedAttribute === 'traits' 
                          ? 'text-emerald-500' 
                          : selectedAttribute === 'metrics' 
                            ? 'text-amber-500' 
                            : 'text-gray-500'
                        }
                      `}
                      onClick={() => setSelectedAttribute("all")}
                    >
                      <svg className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Results list */}
          <div className="flex-1 overflow-y-auto">
            {queriedData.length ? queriedData.map((item: any) => (
              <button
                type="button"
                key={uuid()}
                onClick={() => {
                  setSelected(item);
                  setIsOpen(false);
                }}
                className={classNames(
                  "hover:bg-gray-200 transition-colors duration-100 group text-gray-700",
                  "block w-full relative cursor-pointer select-none py-1.5 pl-3 pr-8 border-b border-gray-100 last:border-b-0",
                  item === selected ? "bg-gray-50" : ""
                )}
              >
                <div className="flex items-center">
                  {item?.source === "segment" ? (
                    <Segment className="h-4 w-4 flex-shrink-0 rounded-full mr-2" />
                  ) : (
                    <He className="h-4 w-4 flex-shrink-0 rounded-full mr-2" />
                  )}
                  <span
                    className={classNames(
                      item === selected ? "font-medium text-gray-900" : "font-normal",
                      "block truncate text-xs"
                    )}
                  >
                    {item?.display_name}
                  </span>
                </div>
                {item === selected ? (
                  <span className={classNames("absolute inset-y-0 right-0 flex items-center pr-2")}>
                    <CheckIcon className="h-3.5 w-3.5 text-primary-600" aria-hidden="true" />
                  </span>
                ) : null}
              </button>
            )) : (
              <Paper className="w-full h-full flex justify-center items-center border-0 shadow-none">
                <Text size="xs" c="dimmed" className="text-center py-6">No matching items found</Text>
              </Paper>
            )}
          </div>
        </div>
      </Menu.Dropdown>
    </Menu>
  );
};

export default SingleSelectDropdown;
